import BackupOutlinedIcon from '@mui/icons-material/BackupOutlined';
import { Box, Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { dashBorder } from '../../constants';
import { useFilesManagement } from '../../hooks/useFilesManagement';
import { FileType } from '../../models/fileType';
import { fileService } from '../../services/fileService';
import AttachmentBox from '../AttachmentBox/AttachmentBox';

interface Props {
  onSaveFiles: (files: File[]) => void;
  onDeleteAttachment?: (fileToRemove: FileType) => void;
  onEditAttachment?: (fileToRemove: FileType) => void;
  attachments?: FileType[];
}

export default function FileDropZone(props: Props): JSX.Element {
  const [attachments, setAttachments] = useState<FileType[]>([]);
  const { files, setFiles, onEditFile, onDeleteFile } = useFilesManagement();
  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    onDrop: (acceptedFiles) => setFiles([...files, ...acceptedFiles])
  });

  useEffect(() => {
    setAttachments(props.attachments ?? []);
  }, [props.attachments]);

  const downloadAttachment = (uuid: string) => {
    fileService.getFileUrl(uuid).then((response) => {
      window.open(response.data, 'blank');
    });
  };

  const onSaveFile = () => {
    props.onSaveFiles(files);
    setFiles([]);
  };

  return (
    <Box
      {...getRootProps()}
      sx={{
        position: 'relative',
        ...dashBorder
      }}
    >
      <input {...getInputProps()} />
      {isDragActive && (
        <Box
          sx={{
            display: 'flex',
            position: 'absolute',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(202, 228, 255, 0.3)',
            borderRadius: 3,
            top: 0,
            left: 0,
            height: '100%',
            width: '100%'
          }}
        >
          <BackupOutlinedIcon fontSize="large" sx={{ mr: 4 }} />
          <p>Drop the files here ...</p>
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" color="grey.900">
          Documents
        </Typography>
        <Button variant="lightBlue" onClick={open}>
          + ADD MORE FILES
        </Button>
      </Box>

      <Box sx={{ minHeight: '100px' }}>
        {attachments.map((attachment) => (
          <AttachmentBox
            key={attachment.id}
            onClickFile={() => downloadAttachment(attachment.uuid)}
            fileName={attachment.fileName}
            onDeleteFile={() =>
              props.onDeleteAttachment
                ? props.onDeleteAttachment(attachment)
                : undefined
            }
            onEditFile={(fileName) =>
              props.onEditAttachment
                ? props.onEditAttachment({ ...attachment, fileName })
                : undefined
            }
          />
        ))}
        {files.map((file) => (
          <AttachmentBox
            key={file.name}
            fileName={file.name}
            onClickFile={() => {
              const href = URL.createObjectURL(file);
              window.open(href, 'blank');
            }}
            onDeleteFile={() => onDeleteFile(file)}
            onEditFile={(fileName) => onEditFile(file, fileName)}
          />
        ))}

        {!files.length && !isDragActive && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: 3
            }}
          >
            <BackupOutlinedIcon fontSize="large" sx={{ mr: 2 }} />
            <i>
              Drag and drop your document OR{' '}
              <span
                onClick={open}
                style={{ color: '#0060C9', cursor: 'pointer' }}
              >
                browse your computer
              </span>
            </i>
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'end'
        }}
      >
        <Button disabled={!files.length} onClick={onSaveFile}>
          Save
        </Button>
      </Box>
    </Box>
  );
}
